import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { convertToTimeZone, dateFormatter, getImageUrl } from "../../../utils/Helper";
import { EventLanguage } from "../../../utils/Languages";
import { RemoveExpireTicketFromCart } from "../../../utils/ApiConstant";
import useHttpHook from "../../../Hooks/HttpHook";
import { updCartData } from "../../../ReduxSlice/Checkout/cartSlice";
import ModalWrapper from "../../Common/Portals/ModalWrapper";
import { showErrorToast } from "../../../utils/notify";
import { buildPublicEventShareUrl } from "../../../utils/eventShareUrl";

const FACT_ICONS = {
  Date: "bi-calendar3",
  Time: "bi-clock",
  Location: "bi-geo-alt",
};

const EventLocation = (props) => {
  const [loading, sendRequest, error] = useHttpHook();
  const [linkCopied, setLinkCopied] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const EventData = useSelector((state) => state.event.data);
  const { userDetails } = useSelector((state) => state.user);
  const [timezone, setTimezone] = useState(null);
  const [organizerAvatarFailed, setOrganizerAvatarFailed] = useState(false);

  const sharedUrl = useMemo(
    () =>
      buildPublicEventShareUrl(props.Id, {
        referralId: userDetails?._id,
      }),
    [props.Id, userDetails?._id]
  );

  useEffect(() => {
    setOrganizerAvatarFailed(false);
  }, [props?.organizer?.profilePic, props?.organizer?._id]);

  useEffect(() => {
    if (error && toast.isActive(error) === false) {
      showErrorToast(error);
      if (error === "You are unAuthrised") {
        window.location.href = `${process.env.REACT_APP_MERCH_URL}/login`;
      }
    }
  }, [error]);

  useEffect(() => {
    if (props?.eventTiming?.startDate && props?.eventTiming?.timeZone) {
      setTimezone(
        convertToTimeZone(
          new Date(props.eventTiming.startDate),
          props.eventTiming.timeZone
        )
      );
    }
  }, [props?.eventTiming?.startDate, props?.eventTiming?.timeZone]);

  const purchaseTickets = async () => {
    const searchParams = new URLSearchParams(window.location.search);
    const referralId = searchParams.get("referralId");

    try {
      const res = await sendRequest(
        `${RemoveExpireTicketFromCart}/${props.Id}?referralId=${referralId}`,
        { method: "DELETE" }
      );
      if (res.status === 1) {
        dispatch(updCartData(res));
        let redirectUrl = `/purchase-tickets/${props.Id}`;
        if (referralId) {
          redirectUrl += `?referralId=${referralId}`;
        }
        navigate(redirectUrl);
        window.location.reload();
      }
    } catch (purchaseError) {
      console.log({ purchaseError });
    }
  };

  const handlePurchaseTickets = () => {
    const searchParams = new URLSearchParams(window.location.search);
    const referralId = searchParams.get("referralId");

    if (props.publicAccess && userDetails === false) {
      const purchaseUrl = `${window.location.origin}/event/purchase-tickets/${props.Id}${
        referralId ? `?referralId=${encodeURIComponent(referralId)}` : ""
      }`;
      const params = new URLSearchParams({
        platform: "event",
        redirect: purchaseUrl,
      });
      if (referralId) {
        params.set("referralId", referralId);
      }
      window.location.href = `${process.env.REACT_APP_MERCH_URL}/login?${params.toString()}`;
      return;
    }

    if (userDetails === false) {
      window.location.href = `${process.env.REACT_APP_MERCH_URL}/login?redirect=${window.location.href}`;
    } else {
      purchaseTickets();
    }
  };

  const handleCopyTextFromArea = async () => {
    const area = document.querySelector("#clipboard-area");
    const value = sharedUrl || area?.value || "";
    if (!value) {
      return;
    }
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      } else if (area) {
        area.focus();
        area.select();
        document.execCommand("copy");
      }
      setLinkCopied(true);
      window.setTimeout(() => setLinkCopied(false), 2000);
    } catch (copyError) {
      console.log({ copyError });
      toast.error("Unable to copy link");
    }
  };

  const dateValue = `${dateFormatter(props?.eventTiming?.startDate)} ${
    props?.eventTiming?.eventDayType === "multi" ? "-" : ""
  }${
    props?.eventTiming?.eventDayType === "multi"
      ? dateFormatter(props.eventTiming.endDate)
      : ""
  }`;

  const timeValue =
    props?.eventTiming?.displayStartTime === 1 &&
    props?.eventTiming?.displayEndTime === 1
      ? `${dateFormatter(props?.eventTiming?.startTime, "time", "h:mma")} - ${dateFormatter(
          props?.eventTiming?.endTime,
          "time",
          "h:mma"
        )} (${timezone})`
      : props?.eventTiming?.displayStartTime === 1
      ? dateFormatter(props?.eventTiming?.startTime, "time", "h:mma")
      : props?.eventTiming?.displayEndTime === 1
      ? dateFormatter(props?.eventTiming?.endTime, "time", "h:mma")
      : "";

  const locationValue = [
    props?.location?.address,
    props?.location?.city,
    props?.location?.state,
    props?.location?.postalCode,
    props?.location?.country,
  ]
    .filter(Boolean)
    .join(", ");

  const mapEmbedUrl = locationValue
    ? `https://maps.google.com/maps?q=${encodeURIComponent(
        locationValue
      )}&hl=en&z=14&output=embed`
    : "";

  const facts = [
    {
      label: EventLanguage.UserEvents.detail.Date,
      value: dateValue,
      icon: FACT_ICONS.Date,
    },
    timeValue && {
      label: EventLanguage.UserEvents.detail.Time,
      value: timeValue,
      icon: FACT_ICONS.Time,
    },
    locationValue && {
      label: EventLanguage.UserEvents.detail.Location,
      value: locationValue,
      icon: FACT_ICONS.Location,
    },
  ].filter(Boolean);

  const organizerName = [props?.organizer?.firstName, props?.organizer?.lastName]
    .filter(Boolean)
    .join(" ");

  const organizerAvatar =
    props?.organizer?.profilePic && !organizerAvatarFailed
      ? getImageUrl(props.organizer.profilePic)
      : "";

  const refundSettings = props.eventData?.settings?.settings?.refundSettings;

  const renderRefundPolicy = () => {
    if (refundSettings === "") {
      return "I don't want to specify my refund policy now. I will respond to attendee refund requests on a case by case basis.";
    }
    if (refundSettings === "0") {
      return "No refunds: We do not offer refunds.";
    }
    return (
      <ul className="event-details-refund-list">
        <li>
          Attendees can cancel tickets up to {refundSettings} day before the
          event start date.
        </li>
        <li>
          {process.env.REACT_APP_MERCHANT_FEE}% + 30 cent of total amount is
          Merchant Fee and that is not refundable
        </li>
      </ul>
    );
  };

  const canPurchase =
    props.isCancelled === false &&
    EventData &&
    new Date(EventData.eventTiming.endDate) >= new Date();

  return (
    <aside className="event-details-sidebar" aria-label="Event summary">
      <div className="event-details-card event-details-card-highlight">
        <h2 className="event-details-card-title">{props.title}</h2>
        <div className="event-details-facts">
          {facts.map((fact) => (
            <div className="event-details-fact" key={fact.label}>
              <span className="event-details-fact-icon" aria-hidden="true">
                <i className={`bi ${fact.icon}`}></i>
              </span>
              <div className="event-details-fact-copy">
                <span className="event-details-fact-label">{fact.label}</span>
                <span className="event-details-fact-value">{fact.value}</span>
              </div>
            </div>
          ))}
        </div>

        {canPurchase ? (
          <button
            type="button"
            className="event-details-btn event-details-btn-block"
            disabled={loading}
            onClick={handlePurchaseTickets}
          >
            <i className="bi bi-ticket-perforated" aria-hidden="true"></i>
            <span>{EventLanguage.UserEvents.detail.Purchase}</span>
          </button>
        ) : props.isCancelled ? (
          <p className="event-details-cancelled">
            This event is no longer available
          </p>
        ) : null}
      </div>

      {organizerName && (
        <div className="event-details-card">
          <h3 className="event-details-card-heading">
            {EventLanguage.UserEvents.detail.Organizer}
          </h3>
          <div className="event-details-organizer">
            <div className="event-details-organizer-avatar" aria-hidden="true">
              {organizerAvatar ? (
                <img
                  src={organizerAvatar}
                  alt=""
                  onError={() => setOrganizerAvatarFailed(true)}
                />
              ) : (
                <i className="bi bi-person-badge"></i>
              )}
            </div>
            <div className="event-details-organizer-copy">
              <div className="event-details-organizer-name">{organizerName}</div>
              {props?.organizer?.email && (
                <a
                  className="event-details-email"
                  href={`mailto:${props.organizer.email}`}
                >
                  {props.organizer.email}
                </a>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="event-details-card">
        <h3 className="event-details-card-heading">
          {EventLanguage.UserEvents.detail?.Map}
        </h3>
        <div className="event-details-map-wrap">
          {mapEmbedUrl ? (
            <iframe
              className="event-details-map"
              title="Event location map"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              src={mapEmbedUrl}
            />
          ) : (
            <div className="event-details-map-empty">Location not available</div>
          )}
        </div>
      </div>

      {props.tags?.length > 0 && (
        <div className="event-details-card">
          <h3 className="event-details-card-heading">
            {EventLanguage.UserEvents.detail.Tags}
          </h3>
          <div className="event-details-tags">
            {props.tags.filter(Boolean).map((tag) => (
              <span className="event-details-tag" key={tag}>
                {tag}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="event-details-card event-details-card-muted">
        <h3 className="event-details-card-heading">Refund Policy</h3>
        <div className="event-details-refund">{renderRefundPolicy()}</div>
      </div>

      {props.sharingModal && (
        <ModalWrapper
          title="Share"
          dialogClassName="event-share-modal-dialog"
          contentClassName="event-share-modal"
          headerClassName="event-share-modal-header"
          bodyClassName="event-share-modal-body"
          footerClassName="event-share-modal-footer"
          closeClass="event-share-modal-btn-close"
          submitClass="event-share-modal-btn-ok"
          clickHandler={() => props.setSharingModal(false)}
          CloseModalHandler={() => props.setSharingModal(false)}
        >
          <p className="event-share-lead">
            Share this event with your friends
          </p>
          <p className="event-share-note">
            You will get {EventData?.settings?.settings?.commissionAmount}%
            commission for sharing this event after matching the requirements of
            privacy policy
          </p>
          <div className="event-share-field">
            <label htmlFor="clipboard-area">Shared Link</label>
            <div className="event-share-input-row">
              <input
                className="event-share-input"
                id="clipboard-area"
                value={sharedUrl || ""}
                readOnly
                title={sharedUrl || ""}
                onFocus={(event) => event.target.select()}
              />
              <button
                type="button"
                className={`event-share-copy-btn${
                  linkCopied ? " is-copied" : ""
                }`}
                onClick={handleCopyTextFromArea}
                aria-label="Copy share link"
              >
                <i
                  className={`fa fa-light ${
                    linkCopied ? "fa-check" : "fa-paste"
                  }`}
                  aria-hidden="true"
                ></i>
              </button>
            </div>
          </div>
        </ModalWrapper>
      )}
    </aside>
  );
};

export default EventLocation;
